import { initSentry } from './sentry.js';

// Initialize Sentry first
initSentry();

import { SplitFlapBoard } from './split-flap.js';
import { WebSocketClient } from './websocket-client.js';
import { OfflineIndicator } from './offline-indicator.js';
import { PlaylistController } from './playlist-controller.js';
import { config } from './config.js';
import type { BoardState } from '@flipboard/shared';

/**
 * Extract board ID from URL path: /b/:id
 */
function getBoardIdFromUrl(): string | null {
  const match = window.location.pathname.match(/^\/b\/([a-z0-9-]+)$/);
  return match ? match[1] : null;
}

/**
 * Extract playlist ID from URL path: /p/:id
 */
function getPlaylistIdFromUrl(): string | null {
  const match = window.location.pathname.match(/^\/p\/([a-z0-9-]+)$/);
  return match ? match[1] : null;
}

async function initPlaylist(playlistId: string): Promise<void> {
  const apiBase = config.apiUrl;

  // Create container
  const container = document.createElement('div');
  container.className = 'container';
  document.body.appendChild(container);

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.id = 'splitFlapCanvas';
  container.appendChild(canvas);

  // Initialize offline indicator
  const offlineIndicator = new OfflineIndicator();

  // Initialize playlist controller
  const playlistController = new PlaylistController(
    playlistId,
    canvas,
    apiBase,
    (connected: boolean) => {
      if (connected) {
        offlineIndicator.hide();
      } else {
        offlineIndicator.show();
      }
    },
    (error: string) => {
      console.error('[App] Playlist error:', error);
      offlineIndicator.hide();
      // Could display error on board or in DOM
      document.body.innerHTML = `
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;color:#888;font-family:system-ui;gap:12px;">
          <p style="font-size:18px;">Error loading playlist</p>
          <p style="font-size:14px;color:#666;">${error}</p>
        </div>
      `;
    }
  );

  // Initialize playlist
  await playlistController.initialize();

  // Handle window resize
  window.addEventListener('resize', () => playlistController.resize());

  // Cleanup
  window.addEventListener('beforeunload', () => {
    playlistController.destroy();
  });
}

async function init(): Promise<void> {
  // Show staging banner if on staging environment
  const stagingBanner = document.getElementById('staging-banner');
  if (stagingBanner && window.location.hostname.includes('staging')) {
    stagingBanner.classList.remove('hidden');
  }

  const boardId = getBoardIdFromUrl();
  const playlistId = getPlaylistIdFromUrl();

  if (!boardId && !playlistId) {
    document.body.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;color:#888;font-family:system-ui;gap:12px;">
        <p style="font-size:18px;">Invalid URL</p>
        <p style="font-size:14px;color:#666;">Use /b/your-board-id or /p/your-playlist-id</p>
      </div>
    `;
    return;
  }

  // Handle playlist URL
  if (playlistId) {
    await initPlaylist(playlistId);
    return;
  }

  // Handle board URL (existing logic)
  await initBoard(boardId!);
}

async function initBoard(boardId: string): Promise<void> {
  // Fetch board info to get watermark and fillScreen settings
  const apiBase = config.apiUrl;

  let showWatermark = true; // Default to showing watermark
  let fillScreen = false; // Default to frame enabled

  try {
    const response = await fetch(`${apiBase}/api/boards/${boardId}`);
    if (response.ok) {
      const data = await response.json();
      showWatermark = data.board.showWatermark ?? true;
      fillScreen = data.board.fillScreen ?? false;
    }
  } catch (error) {
    console.warn('Failed to fetch board info, using default settings:', error);
  }

  // Create container
  const container = document.createElement('div');
  container.className = 'container';
  document.body.appendChild(container);

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.id = 'splitFlapCanvas';
  container.appendChild(canvas);

  // Initialize board (sound off by default, controlled from dashboard)
  let currentFillScreen = fillScreen; // Track fillScreen state for updates
  const board = new SplitFlapBoard(canvas, {
    rows: 6,
    cols: 22,
    speed: 1.0,
    soundEnabled: false,
    volume: 0.3,
    frameEnabled: !fillScreen, // Disable frame when fillScreen is enabled
    showWatermark,
  });

  // Initialize offline indicator
  const offlineIndicator = new OfflineIndicator();

  // Track if this is the first state load (for animation)
  let isFirstLoad = true;

  // Handle resize - need to declare before WebSocket client
  function handleResize(): void {
    // Fill screen mode: use full viewport, otherwise leave some margin
    const maxWidth = currentFillScreen ? window.innerWidth : window.innerWidth * 0.95;
    const maxHeight = currentFillScreen ? window.innerHeight : window.innerHeight * 0.9;
    board.fitToSize(maxWidth, maxHeight);
  }

  // Initialize WebSocket client
  const wsClient = new WebSocketClient({
    boardId,
    onState: (state: BoardState) => {
      console.log('[App] Received state:', state.boardId, state.version);
      if (isFirstLoad) {
        // Animate in the first state for a nice effect
        board.applyStateAnimated(state);
        isFirstLoad = false;
      } else {
        board.applyState(state);
      }
      handleResize();
    },
    onMessage: (text: string, clear?: boolean) => {
      console.log('[App] Received message:', text.substring(0, 50));
      board.applyMessage(text, clear);
    },
    onClear: () => {
      console.log('[App] Received clear');
      board.clear();
    },
    onResize: (rows: number, cols: number) => {
      console.log('[App] Received resize:', rows, cols);
      board.setBoard(rows, cols);
      handleResize();
    },
    onSound: (enabled: boolean) => {
      console.log('[App] Received sound:', enabled);
      board.soundEnabled = enabled;
      // Resume audio context if enabling sound (browser requires user gesture but WS counts)
      if (enabled && board.audioContext?.state === 'suspended') {
        board.audioContext.resume();
      }
    },
    onFillScreen: (enabled: boolean) => {
      console.log('[App] Received fillScreen:', enabled);
      currentFillScreen = enabled;
      board.frameEnabled = !enabled;
      handleResize();
    },
    onConnectionChange: (connected: boolean) => {
      console.log('[App] Connection changed:', connected);
      if (connected) {
        offlineIndicator.hide();
      } else {
        offlineIndicator.show();
      }
    },
    onError: (error: string) => {
      console.log('[App] Connection error:', error);
      // Display error message on the split-flap board
      board.applyMessage(error, true);
      // Hide offline indicator since we're showing the error on the board
      offlineIndicator.hide();
    },
  });

  // Skip cached state - we'll animate in the fresh state from WebSocket
  // This gives a nice flip-flap effect on page load

  // Connect to WebSocket
  wsClient.connect();

  // Handle window resize
  window.addEventListener('resize', handleResize);
  handleResize();

  // Enable audio on first interaction
  document.addEventListener(
    'click',
    () => {
      if (board.audioContext?.state === 'suspended') {
        board.audioContext.resume();
      }
    },
    { once: true }
  );

  // Cleanup
  window.addEventListener('beforeunload', () => {
    wsClient.disconnect();
    board.destroy();
  });
}

// Start
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    init();
  });
} else {
  init();
}
